import { memo } from "react";
import type { FormStore } from "../../store/types";
import { useFormStoreContext } from "../../store/zustand-context";
import type {
  AttributeCollectionId,
  AttributeGroup as AttributeGroupType,
} from "../../types";
import { AttributeGroup } from "../groups/AttributeGroup";
import { DynamicGroupRenderer } from "./DynamicGroupRenderer";
import { useCollectionData } from "./useCollectionData";

interface AttributeCollectionProps {
  collectionId: AttributeCollectionId;
  collectionIndex?: number;
  readOnly?: boolean;
  layout?: "vertical" | "horizontal";
  className?: string;
  "data-testid"?: string;
}

/**
 * 属性コレクションコンポーネント
 * フォームのセクションに当たるコンポーネント。属性グループの集合を管理。
 * 動的コレクションの場合は、グループの追加・削除UIを表示します。
 */
export const AttributeCollection = memo(
  ({
    collectionId,
    collectionIndex = 0,
    readOnly,
    layout = "vertical",
    "data-testid": testId,
  }: AttributeCollectionProps) => {
    const store: FormStore = useFormStoreContext();
    const { collectionData, dynamicGroups } = useCollectionData(
      collectionId,
      store,
    );

    // コレクションが存在しない場合は何も表示しない
    if (!collectionData) {
      return null;
    }

    return (
      <div data-testid={testId} data-collection-id={collectionId}>
        <div className="space-y-4">
          {collectionData.attributeGroupSettings.map(
            (group: AttributeGroupType) => {
              // 動的グループの場合
              if (group.isDynamic) {
                return (
                  <DynamicGroupRenderer
                    key={`dynamic-group-container-${group.id}`}
                    dynamicGroup={dynamicGroups.find(
                      (dg) => dg.group.id === group.id,
                    )}
                    collectionId={collectionId}
                    collectionIndex={collectionIndex}
                    layout={layout}
                    readOnly={readOnly}
                  />
                );
              }

              // 通常のグループの場合
              return (
                <AttributeGroup
                  key={`${collectionId}-${group.id}`}
                  groupId={group.id}
                  groupIndex={0}
                  collectionId={collectionId}
                  data-testid={`group-${group.id}`}
                  collectionIndex={collectionIndex}
                  layout={layout}
                  readOnly={readOnly}
                />
              );
            },
          )}
        </div>
      </div>
    );
  },
);

AttributeCollection.displayName = "AttributeCollection";
